import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Instagram,
  Gift,
  TrendingUp,
  Radio,
  Landmark,
  ChevronRight,
  Zap,
} from "lucide-react";
import { LINKS, ASSETS, SHOW_COPYTRADE } from "@/lib/links";
import usePageMeta from "@/hooks/usePageMeta";
import VideoBackground from "@/components/VideoBackground";

const ALL_HUB_LINKS = [
  {
    id: "giveaway",
    label: "Weekly Giveaway",
    sublabel: "Mystery prize · setiap Ahad",
    to: "/giveaway",
    external: false,
    accent: "#E9FF00",
    Icon: Gift,
    show: true,
  },
  {
    id: "copytrade",
    label: "Copytrade",
    sublabel: "Follow · proof boleh check",
    to: "/copytrade",
    external: false,
    accent: "#00A3FF",
    Icon: TrendingUp,
    show: SHOW_COPYTRADE,
  },
  {
    id: "channel",
    label: "Official Channel",
    sublabel: "Trading dari 2016",
    href: LINKS.officialChannel,
    external: true,
    accent: "#00E5FF",
    Icon: Radio,
    show: true,
  },
  {
    id: "broker",
    label: "No Demo, No Dummy",
    sublabel: "Phyntex Markets",
    href: LINKS.broker,
    external: true,
    accent: "#FF3366",
    Icon: Landmark,
    show: true,
  },
];

const HUB_LINKS = ALL_HUB_LINKS.filter((l) => l.show).map((link, i) => ({
  ...link,
  rank: String(i + 1).padStart(2, "0"),
}));

const BIO_LINES = [
  "Trading dari 2016.",
  "Bukan signal. Bukan janji kosong.",
  SHOW_COPYTRADE
    ? "Copytrade · Giveaway · Community"
    : "Giveaway · Community",
];

function HubMissionButton({ link, delay }) {
  const { Icon } = link;
  const style = {
    "--hub-accent": link.accent,
    "--hub-delay": `${delay}ms`,
  };

  const inner = (
    <>
      <span className="hub-mission__rank font-mono">{link.rank}</span>
      <span className="hub-mission__icon" aria-hidden="true">
        <Icon className="w-4 h-4" strokeWidth={2.25} />
      </span>
      <span className="hub-mission__body">
        <span className="hub-mission__label">{link.label}</span>
        <span className="hub-mission__sub">{link.sublabel}</span>
      </span>
      <ChevronRight className="hub-mission__chev w-4 h-4" aria-hidden="true" />
      <span className="hub-mission__shine" aria-hidden="true" />
    </>
  );

  const className = "hub-mission";

  if (link.external) {
    return (
      <a
        href={link.href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        style={style}
        data-testid={`hub-link-${link.id}`}
      >
        {inner}
      </a>
    );
  }

  return (
    <Link
      to={link.to}
      className={className}
      style={style}
      data-testid={`hub-link-${link.id}`}
    >
      {inner}
    </Link>
  );
}

export default function LinkHub() {
  const [mounted, setMounted] = useState(false);

  usePageMeta({
    title: "LUQMANAN | Official",
    description:
      "LUQMANAN Official. Giveaway, copytrade, official channel & broker link.",
  });

  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <VideoBackground className="hub-page" overlayClassName="bg-black/78">
      <div className="hub-vignette" aria-hidden="true" />

      <div className={`hub-shell ${mounted ? "hub-shell--go" : ""}`} data-testid="link-hub">
        <header className="hub-card">
          <span className="hub-card__corner hub-card__corner--tl" aria-hidden="true" />
          <span className="hub-card__corner hub-card__corner--tr" aria-hidden="true" />
          <span className="hub-card__corner hub-card__corner--bl" aria-hidden="true" />
          <span className="hub-card__corner hub-card__corner--br" aria-hidden="true" />

          <div className="hub-card__tag font-mono">
            <Zap className="w-3 h-3 text-[#E9FF00]" />
            <span>PLAYER CARD</span>
          </div>

          <div className="hub-avatar-wrap">
            <div className="hub-avatar-ring" aria-hidden="true" />
            <div className="hub-avatar-scan" aria-hidden="true" />
            <div className="hub-avatar">
              <img
                src={ASSETS.profilePhoto}
                alt="Luqmanan"
                loading="eager"
                decoding="async"
                fetchPriority="high"
              />
            </div>
          </div>

          <h1 className="hub-name font-display">
            <span className="hub-name__shine" data-text="LUQMANAN">
              LUQMANAN
            </span>
          </h1>

          <div className="hub-status font-mono">
            <span className="hub-status__dot" />
            ONLINE · MY
          </div>

          <div className="hub-bio">
            {BIO_LINES.map((line, i) => (
              <p
                key={line}
                className="hub-bio__line"
                style={{ "--line-i": i }}
              >
                <span className="hub-bio__caret">&gt;</span>
                {line}
              </p>
            ))}
          </div>
        </header>

        <div className="hub-missions">
          <div className="hub-missions__head font-mono">
            <span className="hub-missions__label">MISSION SELECT</span>
            <span className="hub-missions__line" aria-hidden="true" />
          </div>

          <nav className="hub-missions__list" aria-label="LUQMANAN links">
            {HUB_LINKS.map((link, i) => (
              <HubMissionButton key={link.id} link={link} delay={120 + i * 80} />
            ))}
          </nav>
        </div>

        <a
          href={LINKS.instagram}
          target="_blank"
          rel="noopener noreferrer"
          className="hub-ig"
          data-testid="hub-instagram"
          aria-label="Instagram @luqmanan_"
        >
          <Instagram className="w-4 h-4" />
          <span className="font-mono text-[9px] tracking-widest uppercase">@luqmanan_</span>
        </a>
      </div>
    </VideoBackground>
  );
}
